// CRM & Sales module contracts: companies, contacts, deals (pipeline), activities.
import { z } from 'zod';

const Uuid = z.string().uuid();
const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data non valida');
const Email = z.string().trim().toLowerCase().regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, 'Email non valida');

export const DEAL_STATE_CATEGORIES = ['backlog', 'in_progress', 'done', 'canceled'] as const;

export const ACTIVITY_KINDS = ['note', 'call', 'meeting', 'email'] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];
export const ACTIVITY_KIND_LABELS: Record<ActivityKind, string> = {
  note: 'Nota', call: 'Chiamata', meeting: 'Incontro', email: 'Email',
};

/** Seeded on enable: a simple, generic sales pipeline. */
export const DEFAULT_DEAL_STAGES = [
  { name: 'Lead', category: 'backlog' },
  { name: 'Contattato', category: 'in_progress' },
  { name: 'Proposta inviata', category: 'in_progress' },
  { name: 'Negoziazione', category: 'in_progress' },
  { name: 'Vinto', category: 'done' },
  { name: 'Perso', category: 'canceled' },
] as const;

// ------------------------------------------------------------------ scheda cliente (client sheet)
// The report fields shown depend on the company's sector — a marketing client and a SaaS client
// track different things. This is presentation config in code (not per-tenant), like DEFAULT_DEAL_STAGES.
export const CRM_SECTORS = ['marketing', 'saas', 'ecommerce', 'other'] as const;
export type CrmSector = (typeof CRM_SECTORS)[number];
export const CRM_SECTOR_LABELS: Record<CrmSector, string> = {
  marketing: 'Marketing', saas: 'SaaS', ecommerce: 'E-commerce', other: 'Altro',
};

export interface SheetField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'textarea';
  suffix?: string;
}
export const CRM_SECTOR_FIELDS: Record<CrmSector, SheetField[]> = {
  marketing: [
    { key: 'monthlyBudget', label: 'Budget mensile media', type: 'number', suffix: '€' },
    { key: 'platforms', label: 'Piattaforme (Meta, TikTok, Google Ads…)', type: 'text' },
    { key: 'targetAudience', label: 'Target di riferimento', type: 'text' },
    { key: 'kpiTarget', label: 'KPI obiettivo (es. CPL, ROAS, CTR)', type: 'text' },
    { key: 'contractStart', label: 'Inizio contratto', type: 'date' },
    { key: 'contractEnd', label: 'Fine contratto', type: 'date' },
    { key: 'campaignNotes', label: 'Note sulla campagna', type: 'textarea' },
  ],
  saas: [
    { key: 'mrr', label: 'MRR (ricavo mensile ricorrente)', type: 'number', suffix: '€' },
    { key: 'plan', label: 'Piano sottoscritto', type: 'text' },
    { key: 'seats', label: 'Licenze / utenti attivi', type: 'number' },
    { key: 'churnRisk', label: 'Rischio abbandono', type: 'text' },
    { key: 'renewalDate', label: 'Data rinnovo', type: 'date' },
    { key: 'onboardingNotes', label: 'Note di onboarding', type: 'textarea' },
  ],
  ecommerce: [
    { key: 'monthlyRevenue', label: 'Fatturato mensile stimato', type: 'number', suffix: '€' },
    { key: 'aov', label: 'Scontrino medio', type: 'number', suffix: '€' },
    { key: 'platform', label: 'Piattaforma e-commerce', type: 'text' },
    { key: 'mainProducts', label: 'Prodotti principali', type: 'text' },
    { key: 'seasonality', label: 'Stagionalità', type: 'text' },
  ],
  // No dedicated report fields: the free-form "Note" on the company already covers this case.
  other: [],
};

const LinkedinUrl = z.string().trim().max(300).nullable().default(null);

export const CompanyInput = z.object({
  name: z.string().trim().min(1, 'Scrivi un nome').max(200),
  domain: z.string().trim().max(200).nullable().default(null),
  industry: z.string().trim().max(80).nullable().default(null),
  notes: z.string().max(4000).nullable().default(null),
  linkedinUrl: LinkedinUrl,
  sector: z.enum(CRM_SECTORS).nullable().default(null),
  sheetData: z.record(z.string().max(60), z.unknown()).default({}),
});
export type CompanyInput = z.input<typeof CompanyInput>;

export const ContactInput = z.object({
  companyId: Uuid.nullable().default(null),
  firstName: z.string().trim().min(1, 'Scrivi un nome').max(100),
  lastName: z.string().trim().max(100).default(''),
  email: Email.nullable().or(z.literal('').transform(() => null)).default(null),
  phone: z.string().trim().max(40).nullable().default(null),
  roleTitle: z.string().trim().max(100).nullable().default(null),
  notes: z.string().max(4000).nullable().default(null),
  linkedinUrl: LinkedinUrl,
});
export type ContactInput = z.input<typeof ContactInput>;

const dealFields = {
  title: z.string().trim().min(1, 'Scrivi un titolo').max(300),
  companyId: Uuid.nullable(),
  contactId: Uuid.nullable(),
  valueCents: z.number().int().min(0).max(1_000_000_000_00),
  stateId: Uuid,
  probability: z.number().int().min(0).max(100),
  expectedCloseDate: DateOnly.nullable(),
  ownerUserId: Uuid.nullable(),
  lostReason: z.string().max(500).nullable(),
};
export const CreateDealInput = z.object(dealFields).partial({
  companyId: true, contactId: true, valueCents: true, stateId: true, probability: true,
  expectedCloseDate: true, ownerUserId: true, lostReason: true,
});
export type CreateDealInput = z.input<typeof CreateDealInput>;

export const UpdateDealInput = z.object(dealFields).partial();
export type UpdateDealInput = z.input<typeof UpdateDealInput>;

export const MoveDealInput = z.object({ stateId: Uuid, beforeId: Uuid.nullable().optional(), afterId: Uuid.nullable().optional() });

export const ActivityInput = z.object({
  kind: z.enum(ACTIVITY_KINDS),
  body: z.string().trim().min(1, 'Scrivi qualcosa').max(4000),
  companyId: Uuid.nullable().default(null),
  contactId: Uuid.nullable().default(null),
  dealId: Uuid.nullable().default(null),
});
export type ActivityInput = z.input<typeof ActivityInput>;

export const DealStageInput = z.object({
  name: z.string().trim().min(1, 'Scrivi un nome').max(40),
  category: z.enum(DEAL_STATE_CATEGORIES),
});
export type DealStageInput = z.input<typeof DealStageInput>;

// ------------------------------------------------------------------ responses
export interface CrmCompanySummary {
  id: string;
  workspaceId: string;
  name: string;
  domain: string | null;
  industry: string | null;
  sector: CrmSector | null;
  contactCount: number;
  openDealCount: number;
}

export interface CrmCompanyDetail extends CrmCompanySummary {
  notes: string | null;
  linkedinUrl: string | null;
  sheetData: Record<string, unknown>;
  createdAt: string;
  canEdit: boolean;
}

export interface CrmContactSummary {
  id: string;
  workspaceId: string;
  companyId: string | null;
  companyName: string | null;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  roleTitle: string | null;
}

export interface CrmContactDetail extends CrmContactSummary {
  notes: string | null;
  linkedinUrl: string | null;
  createdAt: string;
  canEdit: boolean;
}

export interface DealStage {
  id: string;
  name: string;
  category: (typeof DEAL_STATE_CATEGORIES)[number];
  position: number;
  dealCount: number;
  totalValueCents: number;
}

export interface Deal {
  id: string;
  workspaceId: string;
  title: string;
  valueCents: number;
  company: { id: string; name: string } | null;
  contact: { id: string; firstName: string; lastName: string } | null;
  state: { id: string; name: string; category: DealStage['category'] };
  probability: number;
  expectedCloseDate: string | null;
  owner: { id: string; displayName: string } | null;
  lostReason: string | null;
  createdBy: { id: string; displayName: string };
  createdAt: string;
  updatedAt: string;
  permissions: { edit: boolean; delete: boolean };
}

export interface CrmActivity {
  id: string;
  kind: ActivityKind;
  body: string;
  occurredAt: string;
  createdBy: { id: string; displayName: string };
  company: { id: string; name: string } | null;
  contact: { id: string; firstName: string; lastName: string } | null;
  deal: { id: string; title: string } | null;
}

export interface CrmConfig {
  workspaceId: string;
  enabled: boolean;
  currency: string;
  stages: DealStage[];
  permissions: { view: boolean; create: boolean; edit: boolean; delete: boolean; admin: boolean };
}

// ------------------------------------------------------------------ email (send via Gmail + open tracking)
// Sent through the current person's own connected Gmail (see connectors/google.ts). Whether it was opened
// is approximate by nature (tracking pixel) - see CrmSentEmail.opens and the 0025 migration's own note.

export const SendCrmEmailInput = z.object({
  contactId: Uuid,
  dealId: Uuid.nullable().default(null),
  subject: z.string().trim().min(1, 'Scrivi un oggetto').max(300),
  body: z.string().trim().min(1, 'Scrivi un messaggio').max(20000),
  // Set to reply inside an existing Gmail thread (from the live conversation view) instead of starting a
  // new one: threadId is Gmail's own id, inReplyTo is the replied-to message's `Message-ID` header value.
  threadId: z.string().max(100).nullable().default(null),
  inReplyTo: z.string().max(500).nullable().default(null),
});
export type SendCrmEmailInput = z.input<typeof SendCrmEmailInput>;

export interface CrmEmailOpen {
  openedAt: string;
}

export interface CrmSentEmail {
  id: string;
  contactId: string;
  dealId: string | null;
  fromEmail: string;
  toEmail: string;
  subject: string;
  body: string;
  sentBy: { id: string; displayName: string };
  sentAt: string;
  opens: CrmEmailOpen[];
}

export interface GmailStatus {
  /** Whether the current person has a connected Google account with gmail.send granted. */
  connected: boolean;
  /** Set when a Google connection exists but predates the gmail.send scope - needs reconnecting. */
  needsReconnect: boolean;
  /** Whether gmail.readonly is also granted, so the real Gmail conversation with a contact can be shown. */
  canReadThread: boolean;
  email: string | null;
}

/** One real Gmail message, live-fetched (never stored) when viewing a contact's conversation. */
export interface GmailThreadMessage {
  id: string;
  threadId: string;
  /** The RFC822 `Message-ID` header - pass back as `inReplyTo` on SendCrmEmailInput to reply in-thread. */
  messageId: string | null;
  from: string;
  to: string;
  subject: string;
  date: string | null;
  snippet: string;
  bodyText: string;
  direction: 'in' | 'out';
}
